import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiFetch } from '@/api/client'
import { useSessionStore } from '@/store/sessionStore'
import { useToast } from '@/components/ui/Toast'
import { Spinner } from '@/components/ui/Spinner'
import { LangSwitcher } from '@/components/ui/LangSwitcher'
import { ApiError } from '@/api/client'

interface AuthResponse {
  sessionToken: string
  playerId: string
  displayName: string
  isAdmin: boolean
  lockAt: number
  groupId: string | null
  groupName: string | null
}

type Mode = 'signin' | 'signup'

export function JoinPage() {
  const { t } = useTranslation()
  const { code: urlCode } = useParams<{ code: string }>()

  const [mode, setMode] = useState<Mode>(urlCode ? 'signup' : 'signin')

  // Sign-in fields
  const [siName, setSiName] = useState('')
  const [siPassword, setSiPassword] = useState('')

  // Sign-up fields
  const [suCode, setSuCode] = useState(urlCode ?? '')
  const [suName, setSuName] = useState('')
  const [suEmail, setSuEmail] = useState('')
  const [suPassword, setSuPassword] = useState('')
  const [suConfirm, setSuConfirm] = useState('')
  const [suGroupName, setSuGroupName] = useState<string | null>(null)
  const [suCodeChecking, setSuCodeChecking] = useState(false)
  const [suCodeError, setSuCodeError] = useState('')

  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)

  const navigate = useNavigate()
  const { setSession, token } = useSessionStore()
  const { toast } = useToast()

  useEffect(() => {
    if (token) navigate('/bracket', { replace: true })
  }, [token])

  // Auto-validate invite code from URL
  useEffect(() => {
    if (!urlCode) return
    checkCode(urlCode)
  }, [urlCode])

  async function checkCode(code: string) {
    const trimmed = code.trim()
    if (!trimmed) { setSuGroupName(null); return }
    setSuCodeChecking(true)
    setSuCodeError('')
    try {
      const r = await apiFetch<{ valid: boolean; groupName?: string | null }>(`/invite/${trimmed}`)
      setSuGroupName(r.groupName ?? null)
    } catch (err) {
      setSuCodeError(err instanceof ApiError ? err.message : t('join.invalidCode'))
      setSuGroupName(null)
    } finally {
      setSuCodeChecking(false)
    }
  }

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault()
    if (!siName.trim() || !siPassword) return
    setLoading(true)
    try {
      const data = await apiFetch<AuthResponse>('/login', {
        method: 'POST',
        body: JSON.stringify({ displayName: siName.trim(), password: siPassword }),
      })
      setSession(data.sessionToken, data.playerId, data.displayName, data.isAdmin, data.lockAt, data.groupId, data.groupName)
      toast(t('join.welcome', { name: data.displayName }), 'success')
      navigate('/bracket')
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('common.error'), 'error')
    } finally {
      setLoading(false)
    }
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault()
    if (!suCode.trim()) { setSuCodeError(t('join.invalidCode')); return }
    if (suPassword !== suConfirm) { toast(t('join.passwordMismatch'), 'error'); return }
    if (suPassword.length < 8) { toast(t('join.passwordTooShort'), 'error'); return }
    setLoading(true)
    try {
      const data = await apiFetch<AuthResponse>('/register', {
        method: 'POST',
        body: JSON.stringify({
          code: suCode.trim(),
          displayName: suName.trim(),
          password: suPassword,
          email: suEmail.trim() || undefined,
        }),
      })
      setSession(data.sessionToken, data.playerId, data.displayName, data.isAdmin, data.lockAt, data.groupId, data.groupName)
      toast(t('join.welcome', { name: data.displayName }), 'success')
      navigate('/bracket')
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('common.error'), 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gold-400/5 rounded-full blur-3xl" />
      </div>
      <div className="absolute top-4 right-4"><LangSwitcher /></div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="text-7xl mb-4">🏆</div>
          <h1 className="text-3xl font-black text-white mb-2">{t('join.title')}</h1>
          <p className="text-white/50 text-sm">{t('join.subtitle')}</p>
        </div>

        <div className="card-glow p-8">
          {/* Tab switcher */}
          <div className="flex gap-1 bg-white/5 rounded-lg p-1 mb-6">
            {(['signin', 'signup'] as const).map(m => (
              <button key={m} onClick={() => setMode(m)}
                className={`flex-1 py-2 rounded-md text-sm font-semibold transition-colors ${
                  mode === m ? 'bg-white/15 text-white' : 'text-white/40 hover:text-white/70'
                }`}>
                {t(m === 'signin' ? 'join.signIn' : 'join.createAccount')}
              </button>
            ))}
          </div>

          {mode === 'signin' ? (
            <form onSubmit={handleSignIn} className="flex flex-col gap-4">
              <div>
                <label className="text-sm text-white/70 mb-1.5 block">{t('join.displayName')}</label>
                <input className="input" placeholder={t('join.displayNamePlaceholder')}
                  value={siName} onChange={e => setSiName(e.target.value)}
                  maxLength={30} minLength={2} autoFocus required />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm text-white/70">{t('join.password')}</label>
                  <a href="/forgot-password" className="text-xs text-white/40 hover:text-white/70 transition-colors">
                    {t('join.forgotPassword')}
                  </a>
                </div>
                <div className="relative">
                  <input className="input pr-10" type={showPassword ? 'text' : 'password'}
                    placeholder={t('join.passwordSignInPlaceholder')}
                    value={siPassword} onChange={e => setSiPassword(e.target.value)} required />
                  <button type="button" onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 text-sm">
                    {showPassword ? '🙈' : '👁'}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading || !siName.trim() || !siPassword} className="btn-primary mt-1">
                {loading ? <Spinner size="sm" /> : <span>⚽</span>}
                {t('join.signIn')}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSignUp} className="flex flex-col gap-4">
              {/* Invite code */}
              <div>
                <label className="text-sm text-white/70 mb-1.5 block">{t('join.inviteCode')}</label>
                <div className="relative">
                  <input
                    className={`input pr-8 ${suCodeError ? 'border-red-500/50' : suGroupName ? 'border-emerald-500/50' : ''}`}
                    placeholder={t('join.inviteCodePlaceholder')}
                    value={suCode}
                    onChange={e => { setSuCode(e.target.value); setSuCodeError(''); setSuGroupName(null) }}
                    onBlur={() => suCode.trim() && checkCode(suCode)}
                    required
                  />
                  {suCodeChecking && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2"><Spinner size="sm" /></div>
                  )}
                </div>
                {suCodeError && <p className="text-xs text-red-400 mt-1">{suCodeError}</p>}
                {suGroupName && (
                  <p className="text-xs text-emerald-400 mt-1">✓ {t('join.group')}: {suGroupName}</p>
                )}
              </div>

              <div>
                <label className="text-sm text-white/70 mb-1.5 block">{t('join.displayName')}</label>
                <input className="input" placeholder={t('join.displayNamePlaceholder')}
                  value={suName} onChange={e => setSuName(e.target.value)}
                  maxLength={30} minLength={2} required />
                <p className="text-xs text-white/30 mt-1">{t('join.nameHint')}</p>
              </div>

              <div>
                <label className="text-sm text-white/70 mb-1.5 block">
                  {t('join.email')} <span className="text-white/30">{t('join.emailOptional')}</span>
                </label>
                <input className="input" type="email" placeholder={t('join.emailPlaceholder')}
                  value={suEmail} onChange={e => setSuEmail(e.target.value)} />
              </div>

              <div>
                <label className="text-sm text-white/70 mb-1.5 block">{t('join.password')}</label>
                <div className="relative">
                  <input className="input pr-10" type={showPassword ? 'text' : 'password'}
                    placeholder={t('join.passwordPlaceholder')}
                    value={suPassword} onChange={e => setSuPassword(e.target.value)}
                    minLength={8} required />
                  <button type="button" onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 text-sm">
                    {showPassword ? '🙈' : '👁'}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-sm text-white/70 mb-1.5 block">{t('join.confirmPassword')}</label>
                <input className="input" type={showPassword ? 'text' : 'password'}
                  placeholder={t('join.confirmPasswordPlaceholder')}
                  value={suConfirm} onChange={e => setSuConfirm(e.target.value)} required />
              </div>

              <button type="submit"
                disabled={loading || !suCode.trim() || suName.trim().length < 2 || !suPassword || !suConfirm}
                className="btn-primary mt-1">
                {loading ? <Spinner size="sm" /> : <span>⚽</span>}
                {t('join.createAccount')}
              </button>
            </form>
          )}
        </div>

        <FeatureStrip />
      </div>
    </div>
  )
}

function FeatureStrip() {
  const { t } = useTranslation()
  return (
    <div className="mt-6 grid grid-cols-3 gap-3 text-center">
      {[
        { icon: '🎯', label: t('features.exactScore'), desc: t('features.exactScoreDesc') },
        { icon: '📈', label: t('features.progressive'), desc: t('features.progressiveDesc') },
        { icon: '🏆', label: t('features.championPick'), desc: t('features.championPickDesc') },
      ].map(({ icon, label, desc }) => (
        <div key={label} className="card p-3">
          <div className="text-2xl mb-1">{icon}</div>
          <div className="text-xs font-semibold text-white/80">{label}</div>
          <div className="text-[10px] text-white/40 mt-0.5">{desc}</div>
        </div>
      ))}
    </div>
  )
}
